import { type SiteMaterialDetail, useApiTradeV1TradeSitesRetrieve } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  Form,
  MaterialProductAutocomplete,
  TextField,
  TradeCompanySiteSelector,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { useState } from 'react';
import { Button } from 'ui';

import { useUpdateSiteMaterial } from '~/site-materials/hooks/useUpdateSiteMaterial/useUpdateSiteMaterial';

type Props = {
  siteMaterialId: number;
  siteMaterialDetails: SiteMaterialDetail;
};

const schema = yup.object().shape({
  material_grade_id: yup.number().optional(),
  material_product_id: yup.number().required(),
  material_spec_id: yup.number().optional().nullable(),
  seller_label: yup.string().optional(),
  site_id: yup.number().required(),
});

type FormValues = yup.InferType<typeof schema>;

export const SiteMaterialUpdateForm = ({ siteMaterialDetails, siteMaterialId }: Props) => {
  const form = useForm({
    defaultValues: {
      material_product_id: siteMaterialDetails.material_product?.id,
      material_spec_id: siteMaterialDetails.material_spec.id,
      seller_label: siteMaterialDetails.seller_label || undefined,
      site_id: siteMaterialDetails.site.id,
    },
    resolver: yupResolver(schema),
  });
  const [materialSpecId, setMaterialSpecId] = useState(siteMaterialDetails.material_spec.id);
  // We shouldn't do a second request here, we should use the data from the trade site autocomplete instead.
  // Unfortunately, the autocomplete field doesn't return the BE value received from the initialValue
  // and can't be accessed like the one when the field is updated( onSelectOption).
  const { data: tradeSiteDetails } = useApiTradeV1TradeSitesRetrieve(siteMaterialDetails.site.id);
  const { updateSiteMaterial, isPending } = useUpdateSiteMaterial();
  const onSubmit = async (values: FormValues) => {
    await updateSiteMaterial(siteMaterialId, values.material_product_id, values.seller_label);
  };

  return (
    <Form {...form} onSubmit={onSubmit}>
      <TradeCompanySiteSelector
        name="site_id"
        label="Site"
        tradeCompanyId={siteMaterialDetails.site.trade_company.id}
        disabled
      />
      <AllowedMaterialSpecAutocomplete
        name="material_spec_id"
        label="Material Spec"
        onSelectOption={(option) => setMaterialSpecId(option.id)}
      />
      <MaterialProductAutocomplete
        name="material_product_id"
        label="Material Product"
        country={tradeSiteDetails?.country}
        material_spec_id={materialSpecId}
        required
      />
      <TextField
        label="Seller Label"
        name="seller_label"
        infoTooltip="Seller Label (the grade the seller calls this product, e.g. 95/5)"
      />
      <Button loading={isPending} type="submit">
        Update
      </Button>
    </Form>
  );
};
